import type { BulkEditSkusInput } from '@threadheads/shared';
import { parseCsvText, type CsvParseError } from './csvParse';

/**
 * Products > Bulk edit CSV: column detection for the per-SKU bulk edit.
 *
 * PURE. No `t()`, no React, no tRPC. The Products export is the template.
 * Five columns are ever read: the SKU (`Variant SKU`, or `SKU`), the Default
 * cost (`SKU Default Cost`), its `Currency`, the size (`Option1 Value`, or
 * `Size`) and the color (`Option2 Value`, or `Color`). Every other column
 * (Title, tags, images, and the Variant Cost and Category Cost columns of
 * older exports, ...) is ignored. The browser sends the cell text as it is
 * (blank included); the server parses the cost and resolves the currency,
 * does the diff and counts blanks.
 *
 * Formula guard: the export prefixes `'` on text that starts with
 * `= + - @` (after optional spaces) so a spreadsheet never runs it. Every
 * cell read here drops that one apostrophe again, so an untouched export
 * round-trips to no changes. An apostrophe before anything else is kept.
 *
 * Refused files:
 * - A bare `Default Cost` column: older exports wrote the CATEGORY cost under
 *   that name; reading it would copy every category cost onto every SKU.
 * - A `Style` column: the style (category) also drives stock value and the
 *   storefront push, so it is changed in the app, never from a file.
 * - Any row (with or without a SKU) whose Option1 Name is not Size or whose
 *   Option2 Name is not Color: the file is not laid out like the Products
 *   export (Shopify's own export uses the same headers in any order and names
 *   the options only on a product's first row), so its values could land in
 *   the wrong field.
 */

/** Export + import header for the per-SKU default (garment) cost. */
export const SKU_DEFAULT_COST_HEADER = 'SKU Default Cost';
/**
 * Export + import header for the Default cost's currency. Read back: a blank
 * cell means the org currency, and a file with no Currency column keeps each
 * SKU's stored currency (the item then carries no `currency` key).
 */
export const CURRENCY_HEADER = 'Currency';

/** The server's Zod bounds, so a long cell never 400s the request. */
const MAX_LENGTH = { sku: 64, cost: 64, currency: 64, size: 255, color: 255 } as const;

/** Folded header (lowercase, `_` and spaces collapsed) to canonical column. */
const ALIASES: Record<string, string> = {
  'variant sku': 'sku',
  'sku default cost': 'sku_default_cost',
  'option1 value': 'size',
  'option2 value': 'color',
  'option1 name': 'size_name',
  'option2 name': 'color_name',
};

/** Canonical column per editable field. */
const FIELD_COLUMNS = { cost: 'sku_default_cost', size: 'size', color: 'color' } as const;
type Field = keyof typeof FIELD_COLUMNS;

export type SkuBulkEditCsvError =
  | CsvParseError
  | { code: 'cell_too_long'; lines: number[] }
  | { code: 'option_name_mismatch'; lines: number[] }
  | { code: 'no_editable_column' };

interface SkuBulkEditCsvResult {
  items: BulkEditSkusInput['items'];
  /** 1-based file line for `items[i]`; maps the server's `index` to a row. */
  sourceRows: number[];
  errors: SkuBulkEditCsvError[];
}

/** Drops the export's one formula-guard apostrophe, then trims. */
const unwrap = (cell: string | undefined): string => (cell ?? '').replace(/^'(?=\s*[=+\-@])/, '').trim();

const refuse = (error: SkuBulkEditCsvError): SkuBulkEditCsvResult => ({ items: [], sourceRows: [], errors: [error] });

export function parseSkuBulkEditCsv(
  text: string,
  { oldDefaultCostReason, styleReason }: { oldDefaultCostReason: string; styleReason: string },
): SkuBulkEditCsvResult {
  const parsed = parseCsvText(text, {
    requiredColumns: ['sku'],
    columnAliases: ALIASES,
    rejectHeaders: [
      { header: 'default cost', reason: oldDefaultCostReason },
      { header: 'style', reason: styleReason },
    ],
    padShortRows: true,
    skipBlankRows: true,
  });

  if (parsed.structuredErrors.length > 0) {
    return { items: [], sourceRows: [], errors: parsed.structuredErrors };
  }
  const first = parsed.rawRows[0];
  if (!first) return { items: [], sourceRows: [], errors: [] };

  const fields = (Object.keys(FIELD_COLUMNS) as Field[]).filter((field) => FIELD_COLUMNS[field] in first);
  if (fields.length === 0) return refuse({ code: 'no_editable_column' });
  const hasCurrency = 'currency' in first;

  // Pass 1, every row (SKU or not): the option names must match the export.
  const nameMismatch: number[] = [];
  parsed.rawRows.forEach((raw, i) => {
    const sizeName = unwrap(raw.size_name).toLowerCase();
    const colorName = unwrap(raw.color_name).toLowerCase();
    if ((sizeName !== '' && sizeName !== 'size') || (colorName !== '' && colorName !== 'color')) {
      nameMismatch.push(parsed.sourceRows[i] ?? 0);
    }
  });
  if (nameMismatch.length > 0) return refuse({ code: 'option_name_mismatch', lines: nameMismatch });

  // Pass 2: rows with a SKU become items (product header rows carry none).
  const items: BulkEditSkusInput['items'] = [];
  const sourceRows: number[] = [];
  const tooLong: number[] = [];
  parsed.rawRows.forEach((raw, i) => {
    const sku = unwrap(raw.sku);
    if (!sku) return;
    const line = parsed.sourceRows[i] ?? 0;
    const item: BulkEditSkusInput['items'][number] = { sku };
    let long = sku.length > MAX_LENGTH.sku;
    for (const field of fields) {
      const value = unwrap(raw[FIELD_COLUMNS[field]]);
      if (value.length > MAX_LENGTH[field]) long = true;
      item[field] = value;
    }
    if (hasCurrency) {
      const currency = unwrap(raw.currency);
      if (currency.length > MAX_LENGTH.currency) long = true;
      item.currency = currency;
    }
    if (long) {
      tooLong.push(line);
      return;
    }
    items.push(item);
    sourceRows.push(line);
  });

  if (tooLong.length > 0) return refuse({ code: 'cell_too_long', lines: tooLong });
  return { items, sourceRows, errors: [] };
}
